"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Terminal, Cpu, Sparkles, CircleCheck as CheckCircle2, Search } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const focusAreas = [{
  icon: Terminal,
  title: "SaaS search intent",
  description: "Target high-intent comparison, alternative, and category terms that attract paying software subscribers."
}, {
  icon: Cpu,
  title: "Complex technical frameworks",
  description: "Ensure JavaScript-heavy web apps, headless setups, and dynamic documentation index cleanly."
}, {
  icon: Search,
  title: "Organic product discovery",
  description: "Align your product features with organic problem-solving queries that convert trials into revenue."
}];
export default function IndustryHub() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industryhubhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-14 pb-20 md:pt-20 md:pb-28 border-b border-border">
      {/* Subtle decorative background tints */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-36 h-80 w-80 rounded-full bg-secondary/40 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl mx-auto text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <Badge variant="secondary" className="px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide uppercase border border-border inline-flex items-center gap-2">
              <Sparkles className="size-3.5 text-accent" />
              <span>Independent SEO Consultant · Yassine Essaadi</span>
            </Badge>
          </motion.div>

          {/* Main H1 Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
            SEO strategy for software companies, SaaS businesses, and early-stage startups
          </motion.h1>

          {/* Supporting Copy */}
          <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground font-sans max-w-3xl mx-auto leading-relaxed">
            You work directly with me — no account managers, no junior handoffs — to build custom search
            strategies that address technical product architecture, product-led keyword funnels, and
            sustainable customer acquisition for modern tech companies.
          </motion.p>

          {/* Call to action buttons */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-sans font-medium px-7 shadow-sm transition-[color,background-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-sans font-medium px-7 transition-[color,background-color,border-color]">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </motion.div>

          {/* Direct engagement reassurance */}
          <motion.div variants={itemVariants} className="mt-8 pt-6 border-t border-border/70 flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-xs sm:text-sm text-muted-foreground font-sans">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Direct consultant access</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Tailored SaaS search roadmaps</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Practical technical execution</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Highlight Focus Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {focusAreas.map((area, i) => {
          const IconComponent = area.icon;
          return <div key={area.title} data-index={i} className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <div className="size-11 rounded-lg bg-secondary text-primary flex items-center justify-center mb-5">
                  <IconComponent className="size-5" />
                </div>
                <h2 className="font-serif text-lg font-semibold text-card-foreground mb-2">
                  {area.title}
                </h2>
                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {area.description}
                </p>
              </div>;
        })}
        </motion.div>
      </div>
    </section>;
}